/**
 * Query Key Factory for React Query
 *
 * Provides consistent, hierarchical query keys for React Query caching.
 *
 * Structure: ['entity', 'operation', ...params]
 *
 * Benefits:
 * - Easy invalidation: invalidate all 'campaigns' with queryClient.invalidateQueries(['campaigns'])
 * - Granular invalidation: invalidate specific campaign with queryClient.invalidateQueries(queryKeys.campaigns.detail(id))
 * - Automatic deduplication: React Query deduplicates identical query keys
 * - Type safety and consistency across the application
 *
 * Usage:
 * ```javascript
 * import { queryKeys } from '../API/queryKeys';
 *
 * const { data } = useQuery(
 *   queryKeys.campaigns.detail(campaignId),
 *   () => client.campaigns.fetch(campaignId)
 * );
 *
 * // Invalidate after mutation
 * queryClient.invalidateQueries(queryKeys.campaigns.all);
 * queryClient.invalidateQueries(queryKeys.campaigns.detail(campaignId));
 * ```
 */

export const queryKeys = {
  // ========================= CAMPAIGNS =========================
  campaigns: {
    all: ["campaigns"],
    lists: () => [...queryKeys.campaigns.all, "list"],
    list: (filters) => [...queryKeys.campaigns.lists(), { filters }],
    details: () => [...queryKeys.campaigns.all, "detail"],
    detail: (id) => [...queryKeys.campaigns.details(), id],
    fullDetails: (id) => [...queryKeys.campaigns.all, "fullDetail", id],
    admin: () => [...queryKeys.campaigns.all, "admin"],
    kpi: () => [...queryKeys.campaigns.all, "kpi"],
    timeline: (id) => [...queryKeys.campaigns.all, "timeline", id],
    report: (id) => [...queryKeys.campaigns.all, "report", id],
  },

  // ========================= CREATOR =========================
  creator: {
    all: ["creator"],
    dashboard: (username) => [...queryKeys.creator.all, "dashboard", username],
    payouts: (username) => [...queryKeys.creator.all, "payouts", username],
    campaigns: (username) => [
      ...queryKeys.creator.all,
      "campaigns",
      username,
    ],
    financials: (username) => [
      ...queryKeys.creator.all,
      "financials",
      username,
    ],
    zapfeed: (params = {}) => [...queryKeys.creator.all, "zapfeed", params],
    profile: (username) => [...queryKeys.creator.all, "profile", username],
    settings: (username) => [...queryKeys.creator.all, "settings", username],
    commissions: (username) => [
      ...queryKeys.creator.all,
      "commissions",
      username,
    ],
  },

  // ========================= ADMIN =========================
  admin: {
    all: ["admin"],
    campaigns: () => [...queryKeys.admin.all, "campaigns"],
    creators: () => [...queryKeys.admin.all, "creators"],
    invoices: () => [...queryKeys.admin.all, "invoices"],
    payouts: () => [...queryKeys.admin.all, "payouts"],
    withdrawals: () => [...queryKeys.admin.all, "withdrawals"],
    partnerships: () => [...queryKeys.admin.all, "partnerships"],
    kpi: () => [...queryKeys.admin.all, "kpi"],
  },

  // ========================= USERS =========================
  users: {
    all: ["users"],
    current: () => [...queryKeys.users.all, "current"],
    dashboard: (userId) => [...queryKeys.users.all, "dashboard", userId],
    myTeam: (userId) => [...queryKeys.users.all, "myTeam", userId],
    financials: (userId) => [...queryKeys.users.all, "financials", userId],
    settings: (userId) => [...queryKeys.users.all, "settings", userId],
    accountStatus: (userId) => [
      ...queryKeys.users.all,
      "accountStatus",
      userId,
    ],
    balance: (userId) => [...queryKeys.users.all, "balance", userId],
    spendingLedger: (userId) => [...queryKeys.users.all, "spendingLedger", userId],
  },

  // ========================= COMPANIES =========================
  companies: {
    all: ["companies"],
    detail: (id) => [...queryKeys.companies.all, "detail", id],
  },

  // ========================= TEAMS =========================
  teams: {
    all: ["teams"],
    structure: () => [...queryKeys.teams.all, "structure"],
    spendingSummary: (companyId) => [
      ...queryKeys.teams.all,
      "spendingSummary",
      companyId,
    ],
  },

  // ========================= PAYOUTS =========================
  payouts: {
    all: ["payouts"],
    list: (userId) => [...queryKeys.payouts.all, "list", userId],
    detail: (id) => [...queryKeys.payouts.all, "detail", id],
    creator: (creatorId) => [...queryKeys.payouts.all, "creator", creatorId],
  },

  // ========================= INVOICES =========================
  invoices: {
    all: ["invoices"],
    list: () => [...queryKeys.invoices.all, "list"],
    detail: (id) => [...queryKeys.invoices.all, "detail", id],
  },

  // ========================= CONVERSATIONS =========================
  conversations: {
    all: ["conversations"],
    list: (userId) => [...queryKeys.conversations.all, "list", userId],
    detail: (id) => [...queryKeys.conversations.all, "detail", id],
    unread: (userId) => [...queryKeys.conversations.all, "unread", userId],
  },

  // ========================= PARTNERSHIPS =========================
  partnerships: {
    all: ["partnerships"],
    list: () => [...queryKeys.partnerships.all, "list"],
    detail: (id) => [...queryKeys.partnerships.all, "detail", id],
    creator: (creatorId) => [
      ...queryKeys.partnerships.all,
      "creator",
      creatorId,
    ],
  },

  // ========================= WITHDRAWALS =========================
  withdrawals: {
    all: ["withdrawals"],
    list: () => [...queryKeys.withdrawals.all, "list"],
    detail: (id) => [...queryKeys.withdrawals.all, "detail", id],
  },

  // ========================= BLITZ BOLTS =========================
  blitzbolts: {
    all: ["blitzbolts"],
    forUser: (userId) => [...queryKeys.blitzbolts.all, "forUser", userId],
  },

  // ========================= FILES =========================
  files: {
    all: ["files"],
    list: (params) => [...queryKeys.files.all, "list", params],
    detail: (id) => [...queryKeys.files.all, "detail", id],
  },

  // ========================= CRM =========================
  crm: {
    all: ["crm"],
    contacts: () => [...queryKeys.crm.all, "contacts"],
    contact: (id) => [...queryKeys.crm.all, "contact", id],
  },

  // ========================= CONTRACTS =========================
  contracts: {
    all: ["contracts"],
    list: () => [...queryKeys.contracts.all, "list"],
    detail: (id) => [...queryKeys.contracts.all, "detail", id],
  },
};

/**
 * Helper to build dynamic query keys for edge cases
 *
 * Use this when you need a one-off query key that doesn't fit
 * into the standard factory patterns above.
 *
 * @param {string} entity - The entity type (e.g., 'campaigns', 'users')
 * @param {string} operation - The operation (e.g., 'list', 'detail')
 * @param {...any} params - Additional parameters
 * @returns {Array} Query key array
 *
 * @example
 * buildQueryKey('campaigns', 'search', { query: 'test', status: 'active' })
 * // Returns: ['campaigns', 'search', { query: 'test', status: 'active' }]
 */
export const buildQueryKey = (entity, operation, ...params) => {
  return [entity, operation, ...params];
};

/**
 * Invalidation helper for common patterns
 *
 * Provides shortcuts for invalidating related queries.
 *
 * @example
 * // After creating a campaign, invalidate all campaign lists and admin views
 * invalidatePatterns.campaignCreated(queryClient, campaignId);
 */
export const invalidatePatterns = {
  /**
   * Invalidate after campaign created
   */
  campaignCreated: (queryClient) => {
    queryClient.invalidateQueries(queryKeys.campaigns.all);
    queryClient.invalidateQueries(queryKeys.admin.campaigns());
  },

  /**
   * Invalidate after campaign updated
   */
  campaignUpdated: (queryClient, campaignId) => {
    queryClient.invalidateQueries(queryKeys.campaigns.detail(campaignId));
    queryClient.invalidateQueries(queryKeys.campaigns.lists());
    queryClient.invalidateQueries(queryKeys.admin.campaigns());
  },

  /**
   * Invalidate after campaign deleted
   */
  campaignDeleted: (queryClient) => {
    queryClient.invalidateQueries(queryKeys.campaigns.all);
    queryClient.invalidateQueries(queryKeys.admin.campaigns());
  },

  /**
   * Invalidate after payout created/updated
   */
  payoutChanged: (queryClient, creatorId) => {
    queryClient.invalidateQueries(queryKeys.payouts.all);
    queryClient.invalidateQueries(queryKeys.creator.payouts(creatorId));
    queryClient.invalidateQueries(queryKeys.creator.dashboard(creatorId));
    queryClient.invalidateQueries(queryKeys.creator.financials(creatorId));
    queryClient.invalidateQueries(queryKeys.admin.payouts());
  },

  /**
   * Invalidate after creator profile updated
   */
  creatorProfileUpdated: (queryClient, creatorId) => {
    queryClient.invalidateQueries(queryKeys.creator.profile(creatorId));
    queryClient.invalidateQueries(queryKeys.creator.dashboard(creatorId));
    queryClient.invalidateQueries(queryKeys.admin.creators());
  },

  /**
   * Invalidate after team member added/removed
   */
  teamChanged: (queryClient, userId) => {
    queryClient.invalidateQueries(queryKeys.users.myTeam(userId));
    queryClient.invalidateQueries(queryKeys.teams.structure());
  },

  /**
   * Invalidate zapfeed (after any activity)
   */
  zapfeedChanged: (queryClient) => {
    queryClient.invalidateQueries(queryKeys.creator.zapfeed());
  },
};

export default queryKeys;